import React from 'react';
import { Controller } from 'react-hook-form';
import { add } from 'date-fns';
import InputField from 'Core/components/FormElements/InputField';
import Page from 'Core/components/Page';
import { PrimaryButton } from 'Core/components/Button';
import { Trans, useTranslation } from 'react-i18next';
import Card from 'Core/components/FormElements/Card';
import { Column, Row } from 'Core/components/Layout';
import Tabset from 'Core/components/Tabset/Tabset';
import Tooltip from 'Core/components/Tooltip';
import InfoBox from 'Core/components/InfoBox';
import { useScrollBottom } from 'Core/hooks/useScrollBottom';

import Loader from 'Core/components/Loader';
import CalendarInputField from 'Core/components/FormElements/CalendarInputField';
import useAlertStore, { clearAlertSelector } from 'Core/stores/alert';
import { formatCurrency } from 'Core/utils/numberFormat';
import { getAppConfig } from 'Core/config/appConfig';
import { financingTabNames } from 'Modules/fortiva/config/financingTabNames';

import useSelectOfferForm from 'Modules/fortiva/hooks/useSelectOfferForm';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import useGetApplication from 'Modules/fortiva/hooks/useGetApplication';
import { useGetNextStep } from 'Modules/fortiva/hooks/useGetNextStep';
import { hasOtpMismatch, hasExistingCustomer } from 'Modules/fortiva/utils/correctionStatus';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { useQueryClient } from 'react-query';
import FinalizerOffer from 'Modules/fortiva/FinalizeOffer';

import UpdateAchModal from 'Modules/fortiva/components/UpdateAchModal';

import useTabStore from 'Core/stores/tabs';
import SelectOffer from 'Modules/fortiva/SelectOffer';

const FinancingPreparation = ({ application, routes, customerIdentityNumber }) => {
  document.title = 'Payment Portal - Prepare Application';
  const { dateFormat } = getAppConfig();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scroll = useScrollBottom();
  const clearAlert = useAlertStore(clearAlertSelector);

  const DEFAULT_TAB_CONFIG = [
    {
      id: financingTabNames.DOWN_PAYMENT_AND_DELIVERY,
      label: t('fortiva.financing.tabs.headline-1'),
      pageHeader: t('page.selectFinancingOffer.header'),
      isActive: true,
      isDisabled: false,
    },
    {
      id: financingTabNames.SELECT_OFFER,
      label: t('fortiva.financing.tabs.headline-2'),
      pageHeader: t('page.selectFinancingOffer.header'),
      isActive: false,
      isDisabled: false,
    },
    {
      id: financingTabNames.FINALIZE_OFFER,
      label: t('fortiva.financing.tabs.headline-3'),
      pageHeader: t('page.completeFinancing.header'),
      isActive: false,
      isDisabled: false,
    },
  ];

  const [tabConfig, setTabConfig] = React.useState(DEFAULT_TAB_CONFIG);

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedOffer, setSelectedOffer] = React.useState({
    id: '',
    require_downpayment_percent: 0,
    type: '',
  });
  const [requestId, setRequestId] = React.useState('');
  const [enableSignContract, setEnableSignContract] = React.useState(false);
  const activeTab = useTabStore((state) => state.activeTab);
  const setActiveTab = useTabStore((state) => state.setActiveTab);
  const [activeTabId, setActiveTabId] = React.useState(
    activeTab || financingTabNames.DOWN_PAYMENT_AND_DELIVERY
  );
  const [refetchInterval, setRefetchInterval] = React.useState(false);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = React.useState(false);
  const [isUpdateAchModalOpen, setIsUpdateAchModalOpen] = React.useState(false);
  const [pendingFinalize, setPendingFinalize] = React.useState(null);
  const { data: { amount_to_pay = 0 } = {} } = useGetPaymentInfo();
  const {
    data: getAppData,
    isLoading: isGetAppLoading,
    isFetching: isGetAppFetching,
  } = useGetApplication(requestId);

  const { data: getNextStepData } = useGetNextStep(refetchInterval, refetchInterval);

  const latestApp = getNextStepData || getAppData || application || {};
  const {
    next_action,
    payload: {
      offers,
      selected_offer_id,
      already_paid_amount = 0,
      additional_payment = 0,
      minimum_additional_payment = 0,
      approval = 0,
      down_payment_status,
      links: { app: appLink } = {},
    } = {},
  } = latestApp;

  const isDownPaymentPaid = down_payment_status === 'PAID';

  const priceInfo = latestApp?.payload?.offer_term?.price;
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const [{ delivery_date = today } = {}] = priceInfo?.products
    ? JSON.parse(priceInfo?.products)
    : [];
  const firstTermDownPaymentPercent =
    offers?.installment?.[0]?.terms?.[0]?.require_downpayment_percent || 0;
  const effectiveDownPaymentPercent =
    selectedOffer.require_downpayment_percent || firstTermDownPaymentPercent;
  const exceedsLimit = Boolean(approval) && amount_to_pay > Number(approval);
  const approvalGapMinimum = exceedsLimit ? amount_to_pay - Number(approval) : 0;
  const minimumDownPayment = Math.max(
    amount_to_pay * effectiveDownPaymentPercent,
    minimum_additional_payment,
    already_paid_amount,
    approvalGapMinimum
  );
  const maximumDownPayment = amount_to_pay;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    control,
    reset,
  } = useSelectOfferForm({
    minimumDownPayment,
    maximumDownPayment,
    dateFormat,
  });

  const setDownPaymentLabel = (minAmount, maxAmount) => {
    const downPaymentMinLabel = formatCurrency(minAmount, { withDecimals: false });
    const downPaymentMaxLabel = formatCurrency(maxAmount, { withDecimals: false });
    const hasOfferData = Boolean(offers?.installment?.[0]?.terms?.[0]?.id);
    return hasOfferData ? `Down Payment (${downPaymentMinLabel} - ${downPaymentMaxLabel})` : '';
  };

  const downPaymentLabel = setDownPaymentLabel(minimumDownPayment, maximumDownPayment);

  const inputDownPayment = watch('downPayment') || 0;
  const calculatedAmountToCollect =
    Math.round((inputDownPayment - already_paid_amount) * 100) / 100;

  const openAchWindow = () => {
    if (appLink) {
      window.open(appLink, '_target');
    }
  };
  const navigateToFinalizeTab = () => {
    setTabConfig((currentTabConfig) => {
      return currentTabConfig.map((tab) => {
        tab.isActive = tab.id === financingTabNames.FINALIZE_OFFER;
        setActiveTabId(financingTabNames.FINALIZE_OFFER);
        setEnableSignContract(true);
        return tab;
      });
    });
  };

  const handleAchConfirm = () => {
    navigateToFinalizeTab();
    setIsUpdateAchModalOpen(false);
    openAchWindow();
  };

  const handleAchSkipped = () => {
    navigateToFinalizeTab();
    setIsUpdateAchModalOpen(false);
  };

  React.useEffect(() => {
    setTabConfig((currentTabConfig) => {
      return currentTabConfig.map((tab) => {
        if (tab.id === financingTabNames.FINALIZE_OFFER) {
          tab.isDisabled = isDirty || next_action === financingTabNames.SELECT_OFFER;
        }
        return tab;
      });
    });
    if (down_payment_status === 'PAID') {
      setRefetchInterval(false);
      queryClient.invalidateQueries(cacheKeys.transactionHistory);
    }
  }, [isDirty, next_action, down_payment_status, queryClient]);

  React.useEffect(() => {
    setTabConfig((currentTabConfig) => {
      return currentTabConfig.map((tab) => {
        if (tab.id === financingTabNames.DOWN_PAYMENT_AND_DELIVERY) {
          tab.isDisabled = isDownPaymentPaid;
          setIsUpdateButtonDisabled(isDownPaymentPaid);
        }
        return tab;
      });
    });
    // Once the down payment is collected, the down-payment tab must stay
    // unreachable — land on the finalize tab (also covers page refresh).
    if (isDownPaymentPaid) {
      navigateToFinalizeTab();
    }
  }, [isDownPaymentPaid]);

  React.useEffect(() => {
    setValue('deliveryDate', delivery_date);
    // Enforce the (possibly offer-specific) minimum without clobbering a value the
    // user has already entered. Re-running on selectedOffer.id would otherwise reset
    // the user's down payment the first time an offer gets selected.
    const floor = Math.max(additional_payment, minimumDownPayment);
    const current = Number(getValues('downPayment'));
    if (!Number.isFinite(current) || current < floor) {
      setValue('downPayment', floor);
    }
  }, [
    additional_payment,
    delivery_date,
    minimumDownPayment,
    setValue,
    getValues,
    selectedOffer.id,
  ]);

  const otpMismatchPresent = hasOtpMismatch(latestApp);
  const existingCustomerPresent = hasExistingCustomer(latestApp);

  React.useEffect(() => {
    if (isGetAppFetching) return;

    if (otpMismatchPresent) {
      setTabConfig((currentTabConfig) =>
        currentTabConfig.map((tab) => ({
          ...tab,
          isActive: tab.id === financingTabNames.FINALIZE_OFFER,
          isDisabled: tab.id !== financingTabNames.FINALIZE_OFFER,
        }))
      );
      setActiveTabId(financingTabNames.FINALIZE_OFFER);
      setEnableSignContract(true);
      setIsUpdateAchModalOpen(false);
      return;
    }

    switch (next_action) {
      case financingTabNames.SELECT_OFFER:
        break;
      case 'UPDATE_OFFER':
        setEnableSignContract(true);
        if (priceInfo?.payment_method === '') {
          setIsUpdateAchModalOpen(true);
        } else {
          setIsUpdateAchModalOpen(false);
        }
        break;
      case 'SIGN_CONTRACT':
        setTabConfig((currentTabConfig) => {
          return currentTabConfig.map((tab) => {
            tab.isDisabled = tab.id !== financingTabNames.FINALIZE_OFFER;
            tab.isActive = tab.id === financingTabNames.FINALIZE_OFFER;
            setActiveTabId(financingTabNames.FINALIZE_OFFER);
            setEnableSignContract(true);
            return tab;
          });
        });
        break;
      default:
        break;
    }
  }, [next_action, isGetAppFetching, priceInfo?.payment_method, otpMismatchPresent]);

  const handleTabChange = (tabId) => {
    setTabConfig((currentTabConfig) => {
      return currentTabConfig.map((tab) => ({ ...tab, isActive: tabId === tab.id }));
    });
    setActiveTabId(tabId);
  };

  const onFocus = (event) => event.target.select();

  const onSubmit = (values) => {
    scroll.bottom();
    setActiveTab(activeTabId);
    setPendingFinalize({
      downPayment: Number(values.downPayment) || 0,
      deliveryDate: values.deliveryDate,
      amountToCollect: calculatedAmountToCollect,
    });
    reset(values);
    clearAlert();
    handleTabChange(financingTabNames.SELECT_OFFER);
  };

  if (isLoading) {
    return <Loader message={t('status.loading.initializingFinancing')} />;
  }
  const finalizeProps = {
    application: latestApp,
    customerIdentityNumber,
    routes,
    enableSignContract,
    watch,
    pendingFinalize,
    selectedOffer,
    setRequestId,
    setIsLoading,
    setRefetchInterval,
    onBack: () => handleTabChange(financingTabNames.SELECT_OFFER),
  };
  const selectOfferProps = {
    offers,
    selectedOffer,
    setSelectedOffer,
    enableSignContract,
    navigateToFinalizeTab,
    amount_to_pay,
    already_paid_amount,
    approval,
    routes,
    selected_offer_id,
    downPayment: Number(inputDownPayment) || 0,
    // No way back to the down-payment tab once the down payment is collected.
    onBack: isDownPaymentPaid
      ? null
      : () => handleTabChange(financingTabNames.DOWN_PAYMENT_AND_DELIVERY),
  };

  return (
    <Page
      branding="fortiva"
      title={
        tabConfig.find((tab) => tab.id === activeTabId)?.pageHeader ||
        t('page.completeFinancing.header')
      }
      withStepProgress={false}
      stickyFooter
    >
      {(isGetAppLoading || isGetAppFetching) && (
        <Loader message={t('fortiva.updateApplication.loaderText')} />
      )}
      <Card className="bg-slate-300">
        <Row className="flex-wrap content-center">
          <Column className="md:!flex-col-8 mb-0">
            <h4 className="!mb-0">{t('payment.totalAmountLabel')}</h4>
          </Column>
          <Column className="md:!flex-col-4 text-right mb-0 pr-5 font-bold text-md">
            <Trans
              i18nKey={`{{amount}}`}
              values={{ amount: formatCurrency(amount_to_pay, { withDecimals: true }) }}
            />
          </Column>
        </Row>
      </Card>

      <Card className="bg-stone-100 !pt-0">
        <Tabset tabConfig={tabConfig} onTabChange={handleTabChange} interactive={false} />

        <div>
          {activeTabId === financingTabNames.SELECT_OFFER && (
            <SelectOffer {...selectOfferProps}></SelectOffer>
          )}

          {activeTabId === financingTabNames.DOWN_PAYMENT_AND_DELIVERY && (
            <form name="selectOfferForm" onSubmit={handleSubmit(onSubmit)}>
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-6 py-4 mt-4">
                {existingCustomerPresent ? (
                  <>
                    <h2
                      className="!mt-0.5 !mb-2 text-xl font-serif font-bold text-gray-900"
                      data-testid="title-existing-customer"
                    >
                      Existing account found
                    </h2>
                    <h3 className="!mb-0 text-sm text-gray-500 leading-snug">
                      It looks like this customer already has a Fortiva account on file. Enter a
                      down payment below to continue with their existing account.
                    </h3>
                  </>
                ) : (
                  <>
                    <h2
                      className="!mt-0.5 !mb-2 text-xl font-serif font-bold text-gray-900"
                      data-testid="approval-title"
                    >
                      Congratulations, you&apos;re approved!
                    </h2>
                    <h3 className="!mb-0 text-sm text-gray-500 leading-snug">
                      <div className="mb-4">{t('fortiva.downpayment.terms.note1')}</div>
                      <div>{t('fortiva.downpayment.terms.note2')}</div>
                    </h3>
                  </>
                )}
              </div>

              <h3 className="!mt-4">{t('common.updateDownPayment')}</h3>

              <Card>
                <Row>
                  <Column className="md:!flex-col-4">
                    <div className="flex items-center">
                      <div className="mr-2" data-testid="value-available-credit">
                        {t('common.alreadyPaidAmount')}
                      </div>
                      <Tooltip
                        content={t('common.availableCreditTooltip')}
                        data-testid="tooltip-available-credit"
                      ></Tooltip>
                    </div>

                    <div className="mt-1 text-lg font-semibold">
                      {formatCurrency(already_paid_amount)}
                    </div>
                  </Column>
                  <Column className="md:!flex-col-6 lg:!flex-col-4">
                    <InputField
                      register={register}
                      label={downPaymentLabel}
                      data-testid="input-down-payment"
                      prependText="$"
                      name="downPayment"
                      type="number"
                      disabled={isUpdateButtonDisabled}
                      errors={errors}
                      onFocus={onFocus}
                    />
                  </Column>
                </Row>
                {exceedsLimit && (
                  <InfoBox showBorder>
                    Approved amount ({formatCurrency(approval, { withDecimals: true })}) is lower
                    than the total amount ({formatCurrency(amount_to_pay, { withDecimals: true })}).
                    You could pay the difference of{' '}
                    {formatCurrency(approvalGapMinimum, { withDecimals: true })} using a down
                    payment (using another payment source) or use a different payment method.
                  </InfoBox>
                )}
              </Card>

              <h3 className="!mt-4 hidden">{t('common.updateDeliveryDate')}</h3>

              <Card className="hidden">
                <Row className="justify-between items-end">
                  <Column className="md:!flex-col-4">
                    <Controller
                      name="deliveryDate"
                      control={control}
                      rules={{ required: t('common.requiredFieldDeliveryDate') }}
                      render={({ field: { onChange, onBlur, name, value } }) => (
                        <CalendarInputField
                          label={t('common.deliveryDate')}
                          name={name}
                          value={value}
                          onBlur={onBlur}
                          data-testid="input-delivery-date"
                          onChange={onChange}
                          dayPickerProps={{
                            disabledDays: {
                              before: new Date(),
                              after: add(new Date(), { days: 60 }),
                            },
                          }}
                          errors={errors}
                        />
                      )}
                    />
                  </Column>
                </Row>
              </Card>
              <UpdateAchModal
                isModalOpen={isUpdateAchModalOpen}
                onModalConfirm={handleAchConfirm}
                onModalClose={handleAchSkipped}
              ></UpdateAchModal>
              <Row className="flex justify-end mt-4">
                <Column className="md:!flex-col-4 !flex !items-end">
                  <PrimaryButton
                    block
                    type="submit"
                    data-testid="continue-to-finalize"
                    disabled={isUpdateButtonDisabled}
                  >
                    {t('fortiva.continue')}
                  </PrimaryButton>
                </Column>
              </Row>
            </form>
          )}
          {activeTabId === financingTabNames.FINALIZE_OFFER && (
            <FinalizerOffer {...finalizeProps} />
          )}
        </div>
      </Card>
    </Page>
  );
};

export default FinancingPreparation;
